import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer das Haupt-Dashboard (`/dashboard`, Landing-Page
 * nach Login). Wird ueber den `?`-Button im Hero-Header manuell gestartet —
 * Pattern analog `AVAILABILITY_CALENDAR_HELP_TOUR` (Page-Level, kein Dialog).
 *
 * Fuenf Schritte:
 *  1. Welcome — Widget-Konzept als personalisierbare Uebersicht
 *  2. Edit-Mode — wie der Bearbeitungsmodus eingeschaltet wird
 *  3. Widget-Grid — Drag/Drop + Resize im 12-Spalten-Raster (Step aktiviert
 *     Edit-Mode synthetisch, damit Drag-Griffe sichtbar sind)
 *  4. Widget-Bibliothek — Hinzufuegen/Wiederanzeigen ueber den Plus-Button
 *  5. Refresh — zentraler "Aktualisieren"-Button (Cleanup: Edit-Mode aus)
 *
 * Empty-State: Wenn kein Widget gerendert ist, existiert auch kein
 * `<app-dashboard-container>`-Host. Step 3 skipped sich selbst per `skipIf`,
 * die Tour springt direkt zu Step 4 (Library) — das ist genau der Step, den
 * ein neuer Nutzer ohnehin braucht, um sein Dashboard zu fuellen.
 *
 * Voice-Over: Welcome-Step ohne innere Cues, weil der Spotlight bereits die
 * gesamte Page umrahmt (siehe CONTEXTUAL_HELP_PATTERN.md "Voice-Cue nicht
 * auf dieselbe Region wie der Haupt-Spotlight").
 */

/**
 * Schaltet den Bearbeitungsmodus auf den gewuenschten Zustand. Idempotent —
 * klickt den Edit-Toggle nur, wenn der aktuelle Zustand nicht passt.
 *
 * Erkennung des aktuellen Zustands ueber `.dash-btn.primary`: das ist die
 * Klasse des "Fertig"-Buttons (sichtbar nur wenn `isEditMode=true`). Der
 * ngIf-Wrapper rendert genau einen Button, daher reicht der Klassen-Check.
 */
function ensureEditMode(active: boolean): void {
  const wrapper = document.querySelector(
    `[onboardingAnchor="${AnchorId.DashboardEditToggle}"]`,
  );
  const btn = wrapper?.querySelector('button') as HTMLButtonElement | null;
  if (!btn) return;
  const isCurrentlyActive = btn.classList.contains('primary');
  if (isCurrentlyActive !== active) {
    btn.click();
  }
}

export const DASHBOARD_HELP_TOUR: TourConfig = {
  id: TourFeature.DashboardHelp,
  version: 1,
  title: 'DASHBOARD_HELP_TOUR_TITLE',
  description: 'DASHBOARD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/dashboard',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 70,
  // Klassische Guided Tour (Spotlight + Voice-Over), nicht Help-Mode-Overlay.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'DASHBOARD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.DashboardPage,
          spotlightAnchor: AnchorId.DashboardPage,
          title: 'TUTORIAL.DASHBOARD.WELCOME.TITLE',
          content: 'TUTORIAL.DASHBOARD.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'edit-mode',
          anchor: AnchorId.DashboardEditToggle,
          spotlightAnchor: AnchorId.DashboardEditToggle,
          title: 'TUTORIAL.DASHBOARD.EDIT_MODE.TITLE',
          content: 'TUTORIAL.DASHBOARD.EDIT_MODE.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'widget-grid',
          anchor: AnchorId.DashboardWidgetGrid,
          spotlightAnchor: AnchorId.DashboardWidgetGrid,
          title: 'TUTORIAL.DASHBOARD.WIDGET_GRID.TITLE',
          content: 'TUTORIAL.DASHBOARD.WIDGET_GRID.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          // Aktiviert den Bearbeitungsmodus, damit Drag-Griffe und Resize-
          // Anfasser im Grid sichtbar werden.
          onBeforeShow: () => ensureEditMode(true),
          // Empty-State: kein dashboard-container gerendert → Step skippen,
          // die Tour springt direkt zur Widget-Bibliothek.
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.DashboardWidgetGrid}"]`,
            ),
        },
        {
          id: 'widget-library',
          anchor: AnchorId.DashboardWidgetLibraryButton,
          spotlightAnchor: AnchorId.DashboardWidgetLibraryButton,
          title: 'TUTORIAL.DASHBOARD.WIDGET_LIBRARY.TITLE',
          content: 'TUTORIAL.DASHBOARD.WIDGET_LIBRARY.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          // Idempotent: stellt sicher, dass Edit-Mode an ist (Library-Button
          // ist nur dann gerendert). Greift sowohl beim direkten Sprung aus
          // Step 2 (Skip Step 3 im Empty-State) als auch nach Step 3.
          onBeforeShow: () => ensureEditMode(true),
        },
        {
          id: 'refresh',
          anchor: AnchorId.DashboardRefreshButton,
          spotlightAnchor: AnchorId.DashboardRefreshButton,
          title: 'TUTORIAL.DASHBOARD.REFRESH.TITLE',
          content: 'TUTORIAL.DASHBOARD.REFRESH.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          // Cleanup: Edit-Mode zurueck in den Normalzustand schalten, damit
          // der User nach Tour-Ende nicht versehentlich mit aktivem
          // Bearbeitungsmodus dasteht.
          onBeforeShow: () => ensureEditMode(false),
        },
      ],
    },
  ],
};
